/**
 * Parts Export Component
 * Component منفصل فقط للتصدير
 */
import React, { useState } from 'react';
import { Download } from 'lucide-react';
import styled from 'styled-components';
import { exportPart, exportAllParts } from '../../core/video/VideoExporter';
import { Button } from '../../ui/common/Button';

const StatusMessage = styled.div`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: ${props => {
    if (props.type === 'success') return '#d4edda';
    if (props.type === 'error') return '#f8d7da';
    return '#e7f3ff';
  }};
  color: ${props => {
    if (props.type === 'success') return '#155724';
    if (props.type === 'error') return '#721c24';
    return '#004085';
  }};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 16px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  width: ${props => props.progress || 0}%;
  transition: width 0.3s ease;
`;

const PartsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const PartExportButton = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  text-align: left;
`;

export const PartsExporter = ({ parts, groups, project, onExportComplete }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState(null);

  /**
   * Export single part
   * Now uses the new exportPart function from core/video/VideoExporter
   */
  const handleExportPart = async (part) => {
    setIsExporting(true);
    setExportStatus('Preparing export...');
    
    try {
      // Use the new export function - كل شيء منظم الآن
      const result = await exportPart({ part, project });
      
      if (result.success) {
        setExportStatus('✅ Export successful!');
        if (onExportComplete) {
          onExportComplete({ part, result });
        }
        
        // Show success message
        alert(`✅ Part exported successfully!\n\nSaved to: ${result.filePath}`);
      } else if (result.cancelled) {
        setExportStatus('Export cancelled');
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('❌ Export error:', error);
      setExportStatus(`❌ Error: ${error.message}`);
      alert(`❌ Export Failed\n\n${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Export all parts
   */
  const handleExportAll = async () => {
    setIsExporting(true);
    setExportStatus('Exporting all parts...');
    
    try {
      const result = await exportAllParts({
        parts,
        groups,
        project
      });
      
      if (result.success) {
        setExportStatus('✅ All parts exported!');
        alert('✅ All parts exported successfully!');
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('❌ Export error:', error);
      setExportStatus(`❌ Error: ${error.message}`);
      alert(`❌ Export Failed\n\n${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      {exportStatus && (
        <StatusMessage 
          type={exportStatus.includes('✅') ? 'success' : exportStatus.includes('❌') ? 'error' : 'info'}
        >
          {exportStatus}
        </StatusMessage>
      )}
      
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Button 
          onClick={handleExportAll} 
          disabled={isExporting}
          variant="primary"
          icon={<Download size={16} />}
        >
          {isExporting ? 'Exporting...' : 'Export All'}
        </Button>
      </div>
      
      {isExporting && (
        <ProgressBar>
          <ProgressFill progress={exportProgress} />
        </ProgressBar>
      )}
      
      {parts && parts.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            marginBottom: '12px',
            color: '#212529'
          }}>
            Export Individual Parts
          </h4>
          <PartsList>
            {parts.map(part => (
              <PartExportButton
                key={part.id}
                onClick={() => handleExportPart(part)}
                disabled={isExporting}
                variant="success"
                size="small"
              >
                <Download size={14} />
                {part.name || `Part ${part.startTime}-${part.endTime}`}
              </PartExportButton>
            ))}
          </PartsList>
        </div>
      )}
      
      {(!parts || parts.length === 0) && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#6c757d',
          fontSize: '14px'
        }}>
          No parts available for export
        </div>
      )}
    </div>
  );
};

